import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FeatureTour } from "@/components/ui/feature-tour";
import { getAssetUrl, STORAGE_KEYS } from "@/lib/constants";
import { safeLocalStorage } from "@/lib/safeStorage";
import {
  useStepNavigation,
  useTourStops,
  type FirstRunStep,
} from "./tourStops";

/** Fired on window when the tour closes (finished or skipped); Ruta AI's intro waits for it. */
export const FIRST_RUN_TOUR_FINISHED_EVENT = "ruta:first-run-tour-finished";

/**
 * Fired on window when the tour is asked for again. Asking from Live itself navigates
 * nowhere, so nothing else would make the tour notice the request.
 */
const FIRST_RUN_TOUR_REQUESTED_EVENT = "ruta:first-run-tour-requested";

/** One tour per company: the completion mark FeatureTour keeps is scoped by this key. */
export function firstRunTourKey(orgId: string) {
  return `first-run-${orgId}`;
}

export function firstRunTourPendingKey(orgId: string) {
  return `${STORAGE_KEYS.FIRST_RUN_TOUR_PENDING}_${orgId}`;
}

/**
 * Ask for the tour on the next dashboard visit — what the wizard does when setup
 * finishes, and what "Show me around" (sidebar footer, Settings, or the help menu) does
 * later. Clears the completion mark and any saved place too, so it starts from the
 * welcome, and a second company set up in the same browser gets its own tour.
 */
export function requestFirstRunTour(orgId: string) {
  safeLocalStorage.removeItem(`tour-completed-${firstRunTourKey(orgId)}`);
  // From the welcome, not wherever a walk set aside earlier stopped.
  safeLocalStorage.removeItem(`tour-state-${firstRunTourKey(orgId)}`);
  safeLocalStorage.setItem(firstRunTourPendingKey(orgId), "true");
  window.dispatchEvent(new CustomEvent(FIRST_RUN_TOUR_REQUESTED_EVENT));
}

interface FirstRunTourProps {
  orgId?: string;
}

/**
 * A guided walk for an owner's first arrival after onboarding: the header first —
 * the work queue and the top bar are on every page — then every page in the sidebar,
 * in sidebar order, each opened for real with the page spotlit and its parts — the
 * button to press among them — numbered. The stops themselves live in `tourStops`,
 * shared with the help menu, whose "Tour this page" pulls one up on demand.
 * "Try it yourself" sets the tour aside so the page can be used; "Resume tour" picks
 * up the same step. Opens only when the wizard (or "Show me around") asked for it, closes for
 * good on Done, Skip, or Escape, and Done brings the owner home to Live.
 */
export function FirstRunTour({ orgId }: FirstRunTourProps) {
  // Each request starts a fresh walk from the welcome, even one made while this is
  // already mounted on Live.
  const [run, setRun] = useState(0);
  useEffect(() => {
    const onRequest = () => setRun((n) => n + 1);
    window.addEventListener(FIRST_RUN_TOUR_REQUESTED_EVENT, onRequest);
    return () => window.removeEventListener(FIRST_RUN_TOUR_REQUESTED_EVENT, onRequest);
  }, []);
  const pending =
    !!orgId &&
    safeLocalStorage.getItem(firstRunTourPendingKey(orgId)) === "true";
  const { header, pages } = useTourStops({ enabled: pending });
  const navigate = useNavigate();

  const steps = useMemo<FirstRunStep[]>(() => {
    const list = [...header, ...pages];
    // The opener: a dialog, not a spotlight — the welcome clip with one line under it.
    // It tells the owner the tour is starting rather than asking. Start begins the
    // walk; Later parks it in the corner.
    const welcome: FirstRunStep = {
      id: "welcome",
      route: "/assistant",
      modal: true,
      short: "Welcome",
      title: "You're in. Let's go!",
      content: "One quick lap of your new office — then it's all yours.",
      // The Ruta mark — the same tile as the app icon — rather than a generic glyph.
      icon: (
        <img
          src="/apple-touch-icon.png"
          alt="Ruta"
          className="h-full w-full rounded-lg object-cover"
        />
      ),
      // The welcome clip is the opener. It lives on the assets CDN, not in the bundle;
      // upload a new cut to `onboarding/` in each environment's assets bucket.
      media: {
        src: getAssetUrl("onboarding/welcome-tour.mp4"),
        poster: getAssetUrl("onboarding/welcome-tour-poster.jpg"),
      },
    };
    return [welcome, ...list];
  }, [header, pages]);

  const handleStepId = useStepNavigation(steps);

  if (!orgId || !pending) return null;

  const finish = () => {
    safeLocalStorage.removeItem(firstRunTourPendingKey(orgId));
    window.dispatchEvent(
      new CustomEvent(FIRST_RUN_TOUR_FINISHED_EVENT, { detail: { orgId } }),
    );
  };

  return (
    <FeatureTour
      key={run}
      tourKey={firstRunTourKey(orgId)}
      steps={steps}
      autoStart
      collapsible
      onStepId={handleStepId}
      // Done brings them home to Live; Skip / Escape leaves them on whatever page they stopped on.
      onComplete={() => {
        finish();
        navigate("/assistant");
      }}
      onSkip={finish}
    />
  );
}
